import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PlusIcon } from '@heroicons/react/24/outline';
import type { Bill, BillStatus } from '../../../../billing/types/billing.types';
import { BILL_STATUS_LABELS } from '../../../../billing/types/billing.types';

interface InvoicesSectionProps {
  bills: Bill[];
  loading: boolean;
  createManualBill: () => Promise<Bill>;
}

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);

const formatDate = (dateString: string) =>
  new Date(dateString).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

const getBillStatusColor = (status: BillStatus | string) => {
  switch (status) {
    case 'paid': return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300';
    case 'sent': return 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300';
    case 'approved': return 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/20 dark:text-emerald-300';
    case 'in_review': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-300';
    case 'revision': return 'bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-300';
    case 'overdue': return 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-300';
    case 'cancelled': return 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400';
    default: return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
  }
};

const InvoicesSection: React.FC<InvoicesSectionProps> = ({ bills, loading, createManualBill }) => {
  const navigate = useNavigate();
  const [selectedBillStatus, setSelectedBillStatus] = useState('all');
  const [billCreating, setBillCreating] = useState(false);

  const filteredBills = bills.filter(
    (bill) => selectedBillStatus === 'all' || bill.status === selectedBillStatus
  );

  const handleCreateBill = async () => {
    setBillCreating(true);
    try {
      const bill = await createManualBill();
      navigate(`/billing/invoices/${bill.bill_id}`);
    } catch {
      setBillCreating(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <h4 className="text-lg font-medium text-gray-900 dark:text-white">Invoices</h4>
        <div className="flex gap-2">
          <select
            value={selectedBillStatus}
            onChange={(e) => setSelectedBillStatus(e.target.value)}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm w-full sm:w-auto"
          >
            <option value="all">All Statuses</option>
            {Object.entries(BILL_STATUS_LABELS).map(([key, label]) => (
              <option key={key} value={key}>{label}</option>
            ))}
          </select>
          <button
            onClick={handleCreateBill}
            disabled={billCreating}
            className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 whitespace-nowrap"
          >
            <PlusIcon className="h-4 w-4" />
            {billCreating ? 'Creating...' : 'Create Invoice'}
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin h-6 w-6 border-2 border-blue-500 border-t-transparent rounded-full" />
          <span className="ml-2 text-sm text-gray-500">Loading invoices...</span>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredBills.map((bill) => (
            <div
              key={bill.bill_id}
              className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors cursor-pointer"
              onClick={() => navigate(`/billing/invoices/${bill.bill_id}`)}
            >
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-y-2 gap-x-4">
                <div className="flex-1">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-x-3 gap-y-1">
                    <div className="font-medium text-gray-900 dark:text-white">
                      {bill.bill_number || `INV-${bill.bill_id.slice(0, 8)}`}
                    </div>
                    <span className={`px-2 py-0.5 text-xs rounded-full ${getBillStatusColor(bill.status)} w-fit`}>
                      {BILL_STATUS_LABELS[bill.status] || bill.status}
                    </span>
                  </div>
                  {bill.description && (
                    <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">{bill.description}</div>
                  )}
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    Issued: {formatDate(bill.issue_date)}
                    {bill.due_date && ` \u2022 Due: ${formatDate(bill.due_date)}`}
                  </div>
                </div>
                <div className="text-right flex-shrink-0">
                  <div className="text-lg font-bold text-gray-900 dark:text-white tabular-nums">
                    {formatCurrency(Number(bill.total))}
                  </div>
                  {bill.amount_paid > 0 && (
                    <div className="text-xs text-green-500 tabular-nums">
                      Paid: {formatCurrency(Number(bill.amount_paid))}
                    </div>
                  )}
                  {bill.balance > 0 && (
                    <div className="text-xs text-red-500 tabular-nums">
                      Balance: {formatCurrency(Number(bill.balance))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}

          {filteredBills.length === 0 && (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              No invoices found{selectedBillStatus !== 'all' ? ' matching your criteria' : ' for this engagement'}.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default InvoicesSection;
